/**
 * The fleet feed's pure model (M6 plan P35, spec §8 "Feed and alerts").
 *
 * - **Feed rows:** the captain log lines (the strategic client's `fleetFeed`, newest first) merged with the claim
 *   changes the feature sees (`trackClaims` diffs the corporation's claims between reads; the first read is the
 *   baseline, so a claim held when the session starts is not news). Newest first, at most `limit`.
 * - **Alert actions (the send-wing rule, D-413):** *jump to* is P22's rule (`alertOffers`: attacked, lost or built,
 *   with a system). *Send wing* is offered only when the asset under attack is in the player's current system and the
 *   wing has at least one member: the wing only flies beside the player (D-403), so an asset elsewhere gets no
 *   button. Sent, the wing breaks formation and engages (`breakEngage`), never `defend` (which defends the player).
 *
 * Message rate, except `offersAction`, which the HUD calls per frame (allocation-free).
 */
import { z } from 'zod'
import { alertOffers, type FleetAlertAction } from '../../fleet-link/presence'
import type { FleetAlert, FleetLogEntry } from '../../strategic/types'
import raw from './fleetFeed.json'

const configSchema = z.object({
  comment: z.string().optional(),
  alertActionS: z.number().positive(),
  alertKeep: z.number().int().positive(),
  feedLimit: z.number().int().positive(),
  claimKeep: z.number().int().positive(),
  refreshS: z.number().positive(),
  labels: z.object({
    title: z.string(),
    alerts: z.string(),
    feed: z.string(),
    empty: z.string(),
    noAlerts: z.string(),
    jumpTo: z.string(),
    sendWing: z.string(),
    day: z.string(),
    claimTaken: z.string().includes('{system}'),
    claimLost: z.string().includes('{system}'),
  }),
})
export type FleetFeedConfig = z.infer<typeof configSchema>
export type FleetFeedLabels = FleetFeedConfig['labels']

export function loadFleetFeedConfig(data: unknown = raw): FleetFeedConfig {
  return configSchema.parse(data)
}

// -------------------------------------------------------------------------------------------------
// Feed rows
// -------------------------------------------------------------------------------------------------

export type FeedRowKind = 'log' | 'claimTaken' | 'claimLost'

export interface FeedRow {
  /** Unique within a feed (`log-<id>`, `claim-<seq>`). */
  key: string
  kind: FeedRowKind
  day: number
  /** Strategic time, for the merge order. */
  t: number
  text: string
  systemId: string | null
}

export function logRow(e: Readonly<FleetLogEntry>): FeedRow {
  return { key: `log-${e.id}`, kind: 'log', day: e.day, t: e.t, text: e.text, systemId: e.systemId }
}

/** The claims the feature last saw (null before the first read) and the claim rows it made, newest first. */
export interface ClaimTracker {
  held: ReadonlySet<string> | null
  seq: number
  rows: FeedRow[]
}

export function createClaimTracker(): ClaimTracker {
  return { held: null, seq: 0, rows: [] }
}

/**
 * Diffs the corporation's claims against the last read; returns the new rows (taken, then lost) and keeps them in
 * the tracker (newest first, at most `keep`). The first read only sets the baseline.
 */
export function trackClaims(
  tr: ClaimTracker,
  claims: readonly { systemId: string }[],
  at: { day: number; t: number },
  systemName: (id: string) => string,
  labels: FleetFeedLabels,
  keep: number,
): FeedRow[] {
  const now = new Set(claims.map((c) => c.systemId))
  const was = tr.held
  tr.held = now
  if (was === null) return []
  const fresh: FeedRow[] = []
  const add = (kind: 'claimTaken' | 'claimLost', systemId: string): void => {
    const text = labels[kind].replace('{system}', systemName(systemId))
    fresh.push({ key: `claim-${tr.seq++}`, kind, day: at.day, t: at.t, text, systemId })
  }
  for (const id of now) if (!was.has(id)) add('claimTaken', id)
  for (const id of was) if (!now.has(id)) add('claimLost', id)
  if (fresh.length > 0) tr.rows = [...fresh.slice().reverse(), ...tr.rows].slice(0, keep)
  return fresh
}

/** The feed: log lines and claim rows merged newest first (ties keep claims first), at most `limit`. */
export function feedRows(logs: readonly FleetLogEntry[], claims: readonly FeedRow[], limit: number): FeedRow[] {
  const rows = [...claims, ...logs.map(logRow)]
  rows.sort((a, b) => b.t - a.t || (a.kind === 'log' ? 1 : 0) - (b.kind === 'log' ? 1 : 0))
  return rows.slice(0, limit)
}

// -------------------------------------------------------------------------------------------------
// Alert actions
// -------------------------------------------------------------------------------------------------

/** Where the player is and what the wing has, for the send-wing rule. */
export interface AlertContext {
  /** The player's current system id (the Reach layout), null in transit or outside the Reach. */
  systemId: string | null
  /** Live wing members. */
  wingCount: number
}

/** True when `alert` offers `action` now (the rule above). Allocation-free. */
export function offersAction(alert: Readonly<FleetAlert>, action: FleetAlertAction, ctx: Readonly<AlertContext>): boolean {
  if (!alertOffers(alert, action)) return false
  if (action === 'jumpTo') return true
  return ctx.wingCount > 0 && ctx.systemId !== null && alert.systemId === ctx.systemId
}

/** True when the alert offers any action now. */
export function actionable(alert: Readonly<FleetAlert>, ctx: Readonly<AlertContext>): boolean {
  return offersAction(alert, 'jumpTo', ctx) || offersAction(alert, 'sendWing', ctx)
}

/** The strip's flight hotkeys (bindings.json keyboard `fleetAlertJump`, `fleetAlertWing`). */
export interface AlertKeys {
  jumpTo: readonly string[]
  sendWing: readonly string[]
}

/** "H" for "KeyH", "1" for "Digit1", else the code. */
export function keyLabel(code: string): string {
  if (code.startsWith('Key')) return code.slice(3)
  if (code.startsWith('Digit')) return code.slice(5)
  return code
}

/** The action strip's text for an alert: its text, then the offered actions with their keys. Built on change. */
export function actionText(alert: Readonly<FleetAlert>, ctx: Readonly<AlertContext>, cfg: FleetFeedConfig, keys: Readonly<AlertKeys>): string {
  let s = alert.text
  if (offersAction(alert, 'jumpTo', ctx)) s += ` · [${keyLabel(keys.jumpTo[0])}] ${cfg.labels.jumpTo}`
  if (offersAction(alert, 'sendWing', ctx)) s += ` · [${keyLabel(keys.sendWing[0])}] ${cfg.labels.sendWing}`
  return s
}
